import * as React from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Icon } from '@/ui';

interface Props { value: string; onChange: (html: string) => void; }

export function RichTextEditor({ value, onChange }: Props) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, HTMLAttributes: { rel: 'noopener noreferrer' } }),
    ],
    content: value,
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });

  // Sync external value (e.g. when loading an existing post) without clobbering typing.
  React.useEffect(() => {
    if (editor && !editor.isFocused && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', overflow: 'hidden', background: 'var(--surface-page)' }}>
      <Toolbar editor={editor} />
      <EditorContent editor={editor} className="ar-rte" />
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const setLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link-URL:', prev ?? 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };
  const B = ({ icon, active, onClick, title }: { icon: string; active?: boolean; onClick: () => void; title: string }) => (
    <button
      type="button" title={title} onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 30,
        border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
        background: active ? 'rgba(74,144,226,0.16)' : 'transparent',
        color: active ? 'var(--primary-200)' : 'var(--text-200)',
      }}
    ><Icon name={icon} size={16} /></button>
  );
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, padding: 6, borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-card)' }}>
      <B icon="bold" title="Fett" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
      <B icon="italic" title="Kursiv" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
      <B icon="strikethrough" title="Durchgestrichen" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} />
      <Sep />
      <B icon="heading-2" title="Überschrift 2" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
      <B icon="heading-3" title="Überschrift 3" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />
      <Sep />
      <B icon="list" title="Aufzählung" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
      <B icon="list-ordered" title="Nummerierte Liste" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
      <B icon="quote" title="Zitat" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
      <Sep />
      <B icon="link" title="Link" active={editor.isActive('link')} onClick={setLink} />
      <Sep />
      <B icon="undo-2" title="Rückgängig" onClick={() => editor.chain().focus().undo().run()} />
      <B icon="redo-2" title="Wiederholen" onClick={() => editor.chain().focus().redo().run()} />
    </div>
  );
}
const Sep = () => <span style={{ width: 1, background: 'var(--border-subtle)', margin: '4px 4px' }} />;
